import { EventRecordEventTypes, FeatureTypes } from '@eagle/data-function-types';
import { EventDefinition, EventLocationData, getEventFeature, summarizeEvents } from '@eagle/react-common';
import { toEventLabel, toEventTime } from './trip-history-event-display-utils';
import { filterExcludedTripHistoryEvents, getTripEventsInRange, TripSummaryLike } from './trip-history-event-filtering-utils';

export interface TripEventSummaryItem {
  color: string;
  count: number;
  highestFaultSeverity?: FaultSeverity;
  isFault: boolean;
  label: string;
}

export type FaultSeverity = 'High' | 'Medium' | 'Low';

export interface TripEventSummarizationOptions {
  enabled: boolean;
  notableEvents: unknown[];
  template?: unknown;
}

export interface TripLocationEnrichmentOptions {
  thresholdMinutes?: number | null;
}

export interface TripTimelineProcessedEvent {
  event: EventLocationData;
  eventCount?: number;
  isSummarized: boolean;
  originalEvents?: EventLocationData[];
  summarizationMode?: string;
}

const isFiniteNumber = (value: unknown): value is number => {
  return typeof value === 'number' && Number.isFinite(value);
};

const getFaultSeverity = (event: EventLocationData): FaultSeverity | undefined => {
  if (event.feature !== 'fault/dtc') return;

  const severity = (event.data as { severity?: unknown } | undefined)?.severity;
  if (typeof severity !== 'string') return;

  switch (severity.toLowerCase()) {
    case 'high': return 'High';
    case 'medium': return 'Medium';
    case 'low': return 'Low';
    default: return;
  }
};

const getHigherFaultSeverity = (
  current: FaultSeverity | undefined,
  candidate: FaultSeverity | undefined,
): FaultSeverity | undefined => {
  const severityRank: Record<FaultSeverity, number> = { High: 3, Low: 1, Medium: 2 };
  if (!candidate || (current && severityRank[current] >= severityRank[candidate])) return current;
  return candidate;
};

const isNotableEventDefinition = (value: unknown): value is EventDefinition => {
  if (!value || typeof value !== 'object') return false;
  const event = value as Partial<EventDefinition>;
  return typeof event.featureId === 'string'
    && typeof event.eventTypeId === 'string'
    && typeof event.enabled === 'boolean'
    && typeof event.notableEvent === 'boolean';
};

const isNotableEvent = (event: EventLocationData, notableEvents: EventDefinition[]): boolean => {
  const { feature, featureTypeId, eventTypeId } = event;

  if (featureTypeId === FeatureTypes.EVENT_RECORD_V0 && eventTypeId === EventRecordEventTypes.OCCURRENCE) {
    return notableEvents.some((ne) => (
      ne.enabled
      && ne.notableEvent
      && feature.startsWith(ne.featureId)
      && eventTypeId === ne.eventTypeId
    ));
  }

  return notableEvents.some((ne) => (
    ne.enabled
    && ne.notableEvent
    && ne.featureId === featureTypeId
    && ne.eventTypeId === eventTypeId
  ));
};

const resolveTimelineEventDefinition = (event: EventLocationData, eventDefinitions: EventDefinition[]): EventDefinition | undefined => {
  const exactMatch = eventDefinitions.find((item) => item.featureId === event.featureTypeId && item.eventTypeId === event.eventTypeId);
  if (exactMatch) return exactMatch;

  if (event.featureTypeId === FeatureTypes.EVENT_RECORD_V0 && event.eventTypeId === EventRecordEventTypes.OCCURRENCE) {
    const featurePrefixMatch = eventDefinitions.find((item) => (
      item.eventTypeId === event.eventTypeId
      && item.featureId !== '*'
      && event.feature.startsWith(item.featureId)
    ));
    if (featurePrefixMatch) return featurePrefixMatch;
  }

  return undefined;
};

export const isVisibleTripTimelineEvent = (event: EventLocationData, eventDefinitions: EventDefinition[]): boolean => {
  const definition = resolveTimelineEventDefinition(event, eventDefinitions);
  if (!definition) return false;
  return definition.enabled && definition.notableEvent;
};

export const filterVisibleTripTimelineEvents = (events: EventLocationData[], eventDefinitions: EventDefinition[]): EventLocationData[] => {
  return events.filter((event) => isVisibleTripTimelineEvent(event, eventDefinitions));
};

const toIndividualTimelineEvents = (events: EventLocationData[]): TripTimelineProcessedEvent[] => {
  return events.map((event) => ({
    event,
    isSummarized: false,
  }));
};

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return Boolean(value) && typeof value === 'object';
};

const toEnabledThresholdMinutes = (options?: TripLocationEnrichmentOptions): number | undefined => {
  const thresholdMinutes = options?.thresholdMinutes;
  return isFiniteNumber(thresholdMinutes) && thresholdMinutes > 0 ? thresholdMinutes : undefined;
};

const getLocationRecord = (event: EventLocationData): Record<string, unknown> | undefined => {
  const location = (event.data as { location?: unknown } | undefined)?.location;
  return isRecord(location) ? location : undefined;
};

const hasFiniteLocationCoordinates = (location?: Record<string, unknown>): boolean => {
  if (!location) return false;
  const latitude = location.latitude;
  const longitude = location.longitude;
  return isFiniteNumber(latitude) && isFiniteNumber(longitude);
};

const hasEventLocation = (event: EventLocationData): boolean => {
  return hasFiniteLocationCoordinates(getLocationRecord(event));
};

const isLocationUpdateEventWithLocation = (event: EventLocationData): boolean => {
  return event.eventTypeId === 'location-update' && hasEventLocation(event);
};

const withEventLocation = (event: EventLocationData, sourceEvent: EventLocationData): EventLocationData => {
  const sourceData = isRecord(event.data) ? event.data : {};
  const sourceLocation = (sourceEvent.data as { location?: unknown } | undefined)?.location;

  if (!sourceLocation || !isRecord(sourceLocation)) {
    return event;
  }

  return {
    ...event,
    data: {
      ...sourceData,
      location: sourceLocation,
    },
  } as EventLocationData;
};

export const enrichEventsWithNearestLocationUpdate = (
  events: EventLocationData[],
  options?: TripLocationEnrichmentOptions,
): EventLocationData[] => {
  const thresholdMinutes = toEnabledThresholdMinutes(options);
  if (!thresholdMinutes || events.length < 2) return events;

  const thresholdMillis = thresholdMinutes * 60 * 1000;
  const locationUpdateCandidates = events
    .filter(isLocationUpdateEventWithLocation)
    .map((event) => ({
      event,
      occurredMillis: toEventTime(event.occurred).toMillis(),
    }));

  if (!locationUpdateCandidates.length) return events;

  return events.map((event) => {
    if (hasEventLocation(event)) return event;

    const eventOccurredMillis = toEventTime(event.occurred).toMillis();
    let bestCandidate: EventLocationData | undefined;
    let bestCandidateOccurredMillis = 0;
    let bestDelta = Number.POSITIVE_INFINITY;

    locationUpdateCandidates.forEach((candidate) => {
      const delta = Math.abs(candidate.occurredMillis - eventOccurredMillis);
      if (delta > thresholdMillis) return;

      if (delta < bestDelta) {
        bestDelta = delta;
        bestCandidate = candidate.event;
        bestCandidateOccurredMillis = candidate.occurredMillis;
        return;
      }

      if (delta !== bestDelta || !bestCandidate) return;

      const candidateIsAfter = candidate.occurredMillis >= eventOccurredMillis;
      const bestIsAfter = bestCandidateOccurredMillis >= eventOccurredMillis;
      if (candidateIsAfter && !bestIsAfter) {
        bestCandidate = candidate.event;
        bestCandidateOccurredMillis = candidate.occurredMillis;
        return;
      }

      if (candidateIsAfter === bestIsAfter && candidate.occurredMillis > bestCandidateOccurredMillis) {
        bestCandidate = candidate.event;
        bestCandidateOccurredMillis = candidate.occurredMillis;
      }
    });

    if (!bestCandidate) return event;

    return withEventLocation(event, bestCandidate);
  });
};

export const getTripTimelineProcessedEvents = (
  events: EventLocationData[],
  summarizationOptions?: TripEventSummarizationOptions,
): TripTimelineProcessedEvent[] => {
  const notableEvents = (summarizationOptions?.notableEvents ?? []).filter(isNotableEventDefinition);
  const notableEventsOnly = events.filter((event) => isNotableEvent(event, notableEvents));
  const shouldSummarize = Boolean(
    summarizationOptions?.enabled
    && summarizationOptions.template
    && notableEvents.length,
  );
  const templateRaw = summarizationOptions?.template;
  const template = templateRaw as Record<string, unknown>;
  const summarizeEventsTyped = summarizeEvents as unknown as (
    eventsToSummarize: EventLocationData[],
    summarizationTemplate: Record<string, unknown>,
  ) => unknown;
  const processedEvents = summarizeEventsTyped(notableEventsOnly, template);

  if (!notableEventsOnly.length) {
    return [];
  }

  if (!shouldSummarize || !templateRaw || typeof templateRaw !== 'object' || !Array.isArray(processedEvents)) {
    return toIndividualTimelineEvents(notableEventsOnly);
  }

  const summarized = processedEvents.flatMap<TripTimelineProcessedEvent>((processedEvent) => {
    if (!isRecord(processedEvent)) return [];

    const processedType = processedEvent.type;

    if (processedType === 'individual') {
      if (!isRecord(processedEvent.event)) return [];
      return [{
        event: processedEvent.event as unknown as EventLocationData,
        isSummarized: false,
      }];
    }

    if (processedType !== 'summarized' || !isRecord(processedEvent.representativeEvent)) {
      return [];
    }

    const originalEvents = Array.isArray(processedEvent.originalEvents)
      ? processedEvent.originalEvents.filter(isRecord) as unknown as EventLocationData[]
      : undefined;

    return [{
      event: processedEvent.representativeEvent as unknown as EventLocationData,
      eventCount: isFiniteNumber(processedEvent.eventCount) ? processedEvent.eventCount : undefined,
      isSummarized: true,
      originalEvents,
      summarizationMode: typeof processedEvent.summarizationMode === 'string' ? processedEvent.summarizationMode : undefined,
    }];
  });
  return summarized.sort((first, second) => toEventTime(first.event.occurred).toMillis() - toEventTime(second.event.occurred).toMillis());
};

export const getEventColor = (
  event: Pick<EventLocationData, 'eventTypeId' | 'feature' | 'featureTypeId'>,
  eventDefinitions: EventDefinition[],
): string => {
  return resolveTimelineEventDefinition(event as EventLocationData, eventDefinitions)?.styling.backgroundColor ?? '#1E75D8';
};

export const getEventDisplayLabel = (
  event: Pick<EventLocationData, 'eventTypeId' | 'feature' | 'featureTypeId'>,
  eventDefinitions: EventDefinition[],
): string => {
  if (event.featureTypeId === FeatureTypes.EVENT_RECORD_V0 && event.eventTypeId === EventRecordEventTypes.OCCURRENCE) {
    const definition = resolveTimelineEventDefinition(event as EventLocationData, eventDefinitions);
    const featureId = definition?.featureId ?? event.feature;
    const translatedFeature = getEventFeature(featureId);
    return translatedFeature && translatedFeature !== featureId ? translatedFeature : toEventLabel(featureId);
  }

  return toEventLabel(event.eventTypeId, event.feature);
};

const addToSummaryMap = (
  summaryMap: Map<string, TripEventSummaryItem>,
  event: EventLocationData,
  eventDefinitions: EventDefinition[],
  countIncrement = 1,
): void => {
  const label = getEventDisplayLabel(event, eventDefinitions);
  const existing = summaryMap.get(label);
  const faultSeverity = getFaultSeverity(event);

  if (existing) {
    existing.count += countIncrement;
    existing.highestFaultSeverity = getHigherFaultSeverity(existing.highestFaultSeverity, faultSeverity);
    return;
  }

  summaryMap.set(label, {
    color: getEventColor(event, eventDefinitions),
    count: countIncrement,
    highestFaultSeverity: faultSeverity,
    isFault: event.feature === 'fault/dtc',
    label,
  });
};

export const buildTripEventSummary = (
  trip: TripSummaryLike,
  events: EventLocationData[],
  eventDefinitions: EventDefinition[],
  summarizationOptions?: TripEventSummarizationOptions,
): TripEventSummaryItem[] => {
  const filteredEvents = filterExcludedTripHistoryEvents(getTripEventsInRange(trip, events))
    .filter((event) => isVisibleTripTimelineEvent(event, eventDefinitions))
    .filter((event) => event.eventTypeId !== 'location-update');

  const summaryMap = new Map<string, TripEventSummaryItem>();
  const processedTimelineEvents = getTripTimelineProcessedEvents(filteredEvents, summarizationOptions);

  processedTimelineEvents.forEach((processedEvent) => {
    addToSummaryMap(
      summaryMap,
      processedEvent.event,
      eventDefinitions,
      processedEvent.eventCount ?? 1,
    );
  });

  return Array.from(summaryMap.values());
};
